import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTenantOverview } from '../../hooks/useAdminForms';
import { flattenOrgs } from '../../utils/adminConsole';
import AttachSourceModal from './AttachSourceModal';
import DataSourceCheck, { hasFailingStep, TenantStatusBadge } from './DataSourceCheck';

// One row per dealership: where its data is read from, whether we can read it, and
// whether it is switched on. Dealerships the data team has not handed us a source
// for yet appear here too, so nobody is silently missing.
// Logic: hooks/useAdminForms (shared with the redesign).
function TenantOverview({ orgs, canEdit }) {
    const {
        tenants, loading, error, actionError, busyId, checks, confirmId, setConfirmId, attach, setAttach,
        load, runCheck, activate, detach,
    } = useTenantOverview();

    const dealerOrgs = useMemo(
        () => flattenOrgs(orgs).filter((o) => o.type === 'COG'),
        [orgs],
    );

    const attachedOrgIds = useMemo(() => new Set(tenants.map((t) => t.org_id)), [tenants]);
    const unattached = dealerOrgs.filter((o) => !attachedOrgIds.has(o.id));

    return (
        <>
            <div className="ac-page-head">
                <div>
                    <span className="ac-eyebrow">Platform</span>
                    <h1 className="ac-title">Data sources</h1>
                    <p className="ac-subtitle">
                        Where each dealership’s data is read from. The data team builds and registers the
                        warehouse and hands you the coordinates — you connect to it and switch it on.
                    </p>
                </div>
                {canEdit && (
                    <div className="ac-head-actions">
                        <button
                            className="ac-add"
                            onClick={() => setAttach({ dealerId: unattached[0]?.id })}
                            disabled={dealerOrgs.length === 0}
                        >
                            + Connect source
                        </button>
                    </div>
                )}
            </div>

            {(error || actionError) && <div className="ac-error">{actionError || error}</div>}

            <div className="ac-card">
                <div className="ac-table-wrap">
                    <table className="ac-table">
                        <thead>
                            <tr>
                                <th>Dealership</th>
                                <th>Source endpoint</th>
                                <th>Status</th>
                                <th className="ac-actions-col">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {tenants.map((t) => {
                                const busy = busyId === t.id;
                                const result = checks[t.id];
                                const blocked = hasFailingStep(result?.steps);
                                return (
                                    <React.Fragment key={t.id}>
                                        <tr className={t.status === 'suspended' ? 'ac-row-suspended' : ''}>
                                            <td>
                                                <div className="ac-name">{t.org_name || '—'}</div>
                                                <div className="ac-email">{t.slug}</div>
                                            </td>
                                            <td>
                                                <div className="ac-ds-mono">{t.synapse_server || '—'}</div>
                                                <div className="ac-ds-sub">
                                                    {t.database || 'sqldb-main'}
                                                    {t.lake_account ? ` · ${t.lake_account}` : ' · no storage account'}
                                                </div>
                                            </td>
                                            <td>
                                                <TenantStatusBadge status={t.status} />
                                                {t.is_demo && <span className="ac-ds-demo">Demo</span>}
                                            </td>
                                            <td className="ac-actions">
                                                {confirmId === t.id ? (
                                                    <span className="ac-confirm">
                                                        Detach?
                                                        <button className="ac-confirm-yes" disabled={busy} onClick={() => detach(t)}>Yes</button>
                                                        <button className="ac-confirm-no" onClick={() => setConfirmId(null)}>No</button>
                                                    </span>
                                                ) : (
                                                    <>
                                                        <button className="ac-act" disabled={busy} onClick={() => runCheck(t)}>
                                                            {busy ? 'Checking…' : 'Check'}
                                                        </button>
                                                        <button
                                                            className="ac-act"
                                                            disabled={busy || !canEdit || t.status === 'active' || blocked}
                                                            title={blocked ? 'Resolve the failing checks first.' : undefined}
                                                            onClick={() => activate(t)}
                                                        >
                                                            Activate
                                                        </button>
                                                        <button
                                                            className="ac-act ac-act-danger"
                                                            disabled={busy || !canEdit}
                                                            onClick={() => setConfirmId(t.id)}
                                                        >
                                                            Detach
                                                        </button>
                                                    </>
                                                )}
                                            </td>
                                        </tr>
                                        {(busy || result) && (
                                            <tr className="ac-ds-result-row">
                                                <td colSpan={4}>
                                                    {busy ? (
                                                        <span className="ac-ds-pending">
                                                            <span className="ac-ds-spinner" aria-hidden="true" />
                                                            Querying the dealer database — this can take a few seconds.
                                                        </span>
                                                    ) : (
                                                        <DataSourceCheck result={result} compact />
                                                    )}
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}

                            {unattached.map((o) => (
                                <tr key={o.id} className="ac-ds-unattached">
                                    <td><div className="ac-name">{o.name}</div></td>
                                    <td className="ac-muted">No source connected yet</td>
                                    <td><span className="ac-status ac-ds-badge ac-ds-badge-none">Not connected</span></td>
                                    <td className="ac-actions">
                                        <button className="ac-act" disabled={!canEdit} onClick={() => setAttach({ dealerId: o.id })}>
                                            Attach source
                                        </button>
                                    </td>
                                </tr>
                            ))}

                            {!loading && tenants.length === 0 && unattached.length === 0 && (
                                <tr><td colSpan={4}><div className="ac-empty-state">No dealerships yet.</div></td></tr>
                            )}
                            {loading && (
                                <tr><td colSpan={4}><div className="ac-empty-state">Loading data sources…</div></td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <p className="ac-footnote">
                Sources are built and registered by the data team — this console only connects to what
                already exists. A check that comes back amber can still be activated; the panels that
                depend on the missing datasets simply stay empty until that data lands.
            </p>

            {attach && (
                <AttachSourceModal
                    dealers={dealerOrgs}
                    tenants={tenants}
                    dealerId={attach.dealerId}
                    onClose={() => setAttach(null)}
                    onSaved={load}
                />
            )}
        </>
    );
}

TenantOverview.propTypes = {
    orgs: PropTypes.array.isRequired,
    canEdit: PropTypes.bool,
};

export default TenantOverview;
